import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './MenuBar.css';
import { mapElementPositions } from '../utils/elementMapper';
import supabase from '../utils/supabaseClient';

// Environment check for dev menu visibility
const isDevelopment = process.env.NODE_ENV === 'development';

function MenuBar() {
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const handleClick = (e, section) => {
    e.preventDefault();
    setActiveMenu(null); // Close menu when selecting an item
    setIsMobileMenuOpen(false); // Close mobile menu when selecting an item
    navigate(`/${section}`);
  };

  const handleMenuClick = (menu) => {
    setActiveMenu(activeMenu === menu ? null : menu);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (activeMenu) {
      setActiveMenu(null);
    }
  };

  // Add clickaway handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenu(null);
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleElementMapper = () => {
    mapElementPositions();
    setActiveMenu(null); // Close the Dev menu
    setIsMobileMenuOpen(false); // Close mobile menu
  };

  useEffect(() => {
    let mounted = true;
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (mounted) setIsLoggedIn(!!session);
    };
    checkSession();
    // Listen for auth changes
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (mounted) setIsLoggedIn(!!session);
    });
    return () => {
      mounted = false;
      listener?.subscription.unsubscribe();
    };
  }, []);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsLoggedIn(false);
    setActiveMenu(null);
    setIsMobileMenuOpen(false);
    navigate('/auth');
  };

  return (
    <nav className="menuBar-nav" ref={menuRef}>
      <button 
        className={`menuBar-hamburger ${isMobileMenuOpen ? 'is-active' : ''}`}
        onClick={toggleMobileMenu}
        aria-label="Toggle menu"
      >
        <span className="hamburger-box">
          <span className="hamburger-inner"></span>
        </span>
      </button>
      
      <ul className={`menuBar-list ${isMobileMenuOpen ? 'is-open' : ''}`}>
        <li className="menuBar-item">
          <Link to="/" onClick={e => handleClick(e, '')} className="menuBar-link">
            Home
          </Link>
        </li>
        <li className="menuBar-item menuBar-item--withSubmenu">
          <button onClick={() => handleMenuClick('setup')} className="menuBar-submenuButton">
            Setup
          </button>
          {activeMenu === 'setup' && (
            <ul className="menuBar-submenu">
              <li className="menuBar-item">
                <Link to="/chantersync" onClick={e => handleClick(e, 'chantersync')} className="menuBar-link">
                  ChanterSync
                </Link>
              </li>
              <li className="menuBar-item">
                <Link to="/chanter-test" onClick={e => handleClick(e, 'chanter-test')} className="menuBar-link">
                  Chanter Test
                </Link>
              </li>
            </ul>
          )}
        </li>
        <li className="menuBar-item menuBar-item--withSubmenu">
          <button onClick={() => handleMenuClick('games')} className="menuBar-submenuButton">
            Games
          </button>
          {activeMenu === 'games' && (
            <ul className="menuBar-submenu">
              <li className="menuBar-item">
                <Link to="/blaster" onClick={e => handleClick(e, 'blaster')} className="menuBar-link">
                  Blaster
                </Link>
              </li>
              <li className="menuBar-item">
                <Link to="/battle" onClick={e => handleClick(e, 'battle')} className="menuBar-link">
                  Battle
                </Link>
              </li>
              <li className="menuBar-divider" style={{ borderTop: '1px solid #444', margin: '8px 0' }} />
              <li className="menuBar-item">
                <Link to="/leader-board" onClick={e => handleClick(e, 'leader-board')} className="menuBar-link">
                  Leader Board
                </Link>
              </li>
            </ul>
          )}
        </li>
        <li className="menuBar-item menuBar-item--withSubmenu">
          <button onClick={() => handleMenuClick('learn')} className="menuBar-submenuButton">
            Learn
          </button>
          {activeMenu === 'learn' && (
            <ul className="menuBar-submenu">
              <li className="menuBar-item">
                <Link to="/instruction" onClick={e => handleClick(e, 'instruction')} className="menuBar-link">
                  Instruction
                </Link>
              </li>
              <li className="menuBar-item">
                <Link to="/flashcards" onClick={e => handleClick(e, 'flashcards')} className="menuBar-link">
                  Flash Cards
                </Link>
              </li>
              <li className="menuBar-item">
                <Link to="/chanter-render" onClick={e => handleClick(e, 'chanter-render')} className="menuBar-link">
                  Chanter View
                </Link>
              </li>
              <li className="menuBar-item">
                <Link to="/basic-notes" onClick={e => handleClick(e, 'basic-notes')} className="menuBar-link">
                  Basic Notes
                </Link>
              </li>
              <li className="menuBar-item">
                <Link to="/gracenotes" onClick={e => handleClick(e, 'gracenotes')} className="menuBar-link">
                  Gracenotes
                </Link>
              </li>
            </ul>
          )}
        </li>
        <li className="menuBar-item menuBar-item--withSubmenu">
          <button onClick={() => handleMenuClick('tools')} className="menuBar-submenuButton">
            Tools
          </button>
          {activeMenu === 'tools' && (
            <ul className="menuBar-submenu">
              <li className="menuBar-item">
                <Link to="/tuner" onClick={e => handleClick(e, 'tuner')} className="menuBar-link">
                  Tuner
                </Link>
              </li>
              <li className="menuBar-item">
                <Link to="/metronome" onClick={e => handleClick(e, 'metronome')} className="menuBar-link">
                  Metronome
                </Link>
              </li>
            </ul>
          )}
        </li>
        <li className="menuBar-item">
          <Link to="/aisheetnovel" onClick={e => handleClick(e, 'aisheetnovel')} className="menuBar-link">
            Sheet Music
          </Link>
        </li>
        <li className="menuBar-item">
          <Link to="/resources" onClick={e => handleClick(e, 'resources')} className="menuBar-link">
            Resources
          </Link>
        </li>
        <li className="menuBar-item">
          <Link to="/contact" onClick={e => handleClick(e, 'contact')} className="menuBar-link">
            Contact
          </Link>
        </li>
        <li className="menuBar-item menuBar-item--withSubmenu">
          <button onClick={() => handleMenuClick('help')} className="menuBar-submenuButton">
            Help
          </button>
          {activeMenu === 'help' && (
            <ul className="menuBar-submenu">
              <li className="menuBar-item">
                <Link to="/microphone-help" onClick={e => handleClick(e, 'microphone-help')} className="menuBar-link">
                  Microphone Help
                </Link>
              </li>
              <li className="menuBar-item">
                <Link to="/about-us" onClick={e => handleClick(e, 'about-us')} className="menuBar-link">
                  About Us
                </Link>
              </li>
              <li className="menuBar-item">
                <Link to="/feature-request" onClick={e => handleClick(e, 'feature-request')} className="menuBar-link">
                  Feature Request
                </Link>
              </li>
              <li className="menuBar-item">
                <Link to="/settings" onClick={e => handleClick(e, 'settings')} className="menuBar-link">
                  Settings
                </Link>
              </li>
              {isLoggedIn && (
                <li className="menuBar-item">
                  <button onClick={handleLogout} className="menuBar-link" style={{ background: 'none', border: 'none', padding: 0, color: '#fff', cursor: 'pointer', textAlign: 'left', width: '100%' }}>
                    Log out
                  </button>
                </li>
              )}
            </ul>
          )}
        </li>
        {isDevelopment && (
          <li className="menuBar-item menuBar-item--withSubmenu">
            <button onClick={() => handleMenuClick('dev')} className="menuBar-submenuButton">
              Dev
            </button>
            {activeMenu === 'dev' && (
              <ul className="menuBar-submenu">
                <li className="menuBar-item">
                  <Link to="/render-test" onClick={e => handleClick(e, 'render-test')} className="menuBar-link">
                    Test
                  </Link>
                </li>
                <li className="menuBar-item">
                  <Link to="/animate-song" onClick={e => handleClick(e, 'animate-song')} className="menuBar-link">
                    Animate Song
                  </Link>
                </li>
                <li className="menuBar-item">
                  <Link to="/play-along" onClick={e => handleClick(e, 'play-along')} className="menuBar-link">
                    Play Along
                  </Link>
                </li>
                <li className="menuBar-item">
                  <Link to="/fire-demo" onClick={e => handleClick(e, 'fire-demo')} className="menuBar-link">
                    Fire Effect
                  </Link>
                </li>
                <li className="menuBar-item">
                  <Link to="/template1" onClick={e => handleClick(e, 'template1')} className="menuBar-link">
                    Template1
                  </Link>
                </li>
                <li className="menuBar-item">
                  <Link to="/template2" onClick={e => handleClick(e, 'template2')} className="menuBar-link">
                    Template2
                  </Link>
                </li>
                <li className="menuBar-item">
                  <Link to="/database-test" onClick={e => handleClick(e, 'database-test')} className="menuBar-link">
                    Database Test
                  </Link>
                </li>
                <li className="menuBar-item">
                  <Link to="/blaster-high-scores" onClick={e => handleClick(e, 'blaster-high-scores')} className="menuBar-link">
                    Blaster High Scores
                  </Link>
                </li>
                <li className="menuBar-item">
                  <Link to="/ai-test" onClick={e => handleClick(e, 'ai-test')} className="menuBar-link">
                    AI Test
                  </Link>
                </li>
                <li className="menuBar-item">
                  <button onClick={handleElementMapper} className="menuBar-link">
                    Map Elements
                  </button>
                </li>
              </ul>
            )}
          </li>
        )}
      </ul>
    </nav>
  );
}

export default MenuBar;
